import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { faker } from '@faker-js/faker';
// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography, TextField, Button,  CardContent, Card, CardActions, Box, styled, Paper, Divider } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import { red, yellow, orange, cyan } from '@mui/material/colors';

import MockData from './MockData';



// components
import Iconify from '../components/iconify';
// sections
import {
  AppTasks,
  AppNewsUpdate,
  AppOrderTimeline,
  AppCurrentVisits,
  AppWebsiteVisits,
  AppTrafficBySite,
  AppWidgetSummary,
  AppCurrentSubject,
  AppConversionRates,
} from '../sections/@dashboard/app';

const errorcolor = red[500]; // #f44336
const alertcolor = yellow.A200; // #e040fb
const featurecolor = yellow.A200; // #e040fb (alternative method)

// ----------------------------------------------------------------------

export default function DashboardAppPage() {
  const theme = useTheme();
  const [url, setUrl] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUrlChange = (event) => {
    setUrl(event.target.value);
  };

  const errorCount = MockData.categories.error.count;
  const contrastCount = MockData.categories.contrast.count;
  const alertCount = MockData.categories.alert.count;
  const featureCount = MockData.categories.feature.count;

  const errorAltCount = MockData.categories.error.items.alt_missing.count;
  const errorLabelCount = MockData.categories.error.items.label_missing.count;
  const errorButtonCount = MockData.categories.error.items.button_empty.count;
  const errorLinkCount = MockData.categories.error.items.link_empty.count;
  const errorAriaReference = MockData.categories.error.items.aria_reference_broken.count;


  const alertH1Count = MockData.categories.alert.items.h1_missing.count;
  const alertHeadingCount = MockData.categories.alert.items.heading_skipped.count;
  const alertLinkCount = MockData.categories.alert.items.link_redundant.count;
  const alertTitleCount = MockData.categories.alert.items.title_redundant.count;

 const handleCheckAccessibility = () => {
    setIsLoading(true);
    setTimeout(() => {
      setData(MockData);
      setIsLoading(false);
    }, 2000);
  }


  const bull = (
  <Box
    component="span"
    sx={{ display: 'inline-block', mx: '2px', transform: 'scale(0.8)' }}
  >
    •
  </Box>
);


  return (
    <>
      <Helmet>
        <title> Dashboard | Web Accessibility Nordic </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h2" sx={{ mb: 5 }} color={'#00ad45'}>
          Nordic Web Accessibility Checker
        </Typography>
          <TextField
            label="Please enter web url"
            value={url}
            onChange={handleUrlChange}
            fullWidth
            margin="normal"
            variant="outlined"
          />
          <Button size="large" sx={{mb:2}} variant="contained" startIcon={<AccessibilityIcon />} color="primary" onClick={handleCheckAccessibility}>
            Check Accessibility
          </Button>
          {isLoading && <LinearProgress />}

        {!isLoading && data &&(
          <div>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Critical Errors" total={errorCount} color="error" icon={'material-symbols:error-rounded'} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Total Alerts" total={alertCount} color="warning" icon={'ic:twotone-nearby-error'} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Total Features Errors" total={featureCount} color="secondary"  icon={'pajamas:feature-flag'} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Contrast Errors" total={contrastCount} color="primary" icon={'ri:contrast-2-fill'} />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <AppCurrentVisits
                title="Accessibility Errors"
                chartData={[
                  { label: 'Critical Errors', value: errorCount },
                  { label: 'Total Alerts', value: alertCount },
                  { label: 'Feature Errors', value: featureCount },
                  { label: 'Contrast Errors', value: contrastCount},
                ]}
                chartColors={[
                  theme.palette.primary.main,
                  theme.palette.info.main,
                  theme.palette.warning.main,
                  theme.palette.error.main,
                ]}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={8}>
              <Paper elevation={24}>
              <Typography align="center" variant="h3" color="primary" sx={{ mb: 5, pt:3 }}> Accessibility Reports</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>        
                    <Card sx={{ minWidth: 275 }}>
                        <CardContent>
                          <Typography  variant="h4" color={'#b71c1c'}>
                            Critical Error Details
                          </Typography>
                          <Typography sx={{ mb: 1.5 }} color="text.secondary">
                            {errorCount} errors
                          </Typography>
                          <Divider />
                          <Typography variant="h6">
                           &#x2022; Missing Alternative Text ({errorAltCount})
                            <br />
                            &#x2022; Missing from Label ({errorLabelCount})
                            <br />
                            &#x2022; Empty Button ({errorButtonCount})
                            <br />
                            &#x2022; Empty Link ({errorLinkCount})
                            <br />
                            &#x2022; Broken ARIA reference ({errorAriaReference})
                            <br />
                            
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button size="small">Details</Button>
                        </CardActions>
                    </Card>
                    
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ minWidth: 275 }}>
                        <CardContent>
                          <Typography variant="h4" component="div" color={'#ef6c00'}>
                            Total Alert Details
                          </Typography>
                          <Typography sx={{ mb: 1.5 }} color="text.secondary">
                            {alertCount} errors
                          </Typography>
                          <Divider />
                          <Typography variant="h6">
                             &#x2022; Missing first level heading ({alertH1Count})
                            <br />
                            &#x2022; Skipped heading level ({alertHeadingCount})
                            <br />
                            &#x2022; Redundant link ({alertLinkCount})
                            <br />
                            &#x2022; Redundant title text ({alertTitleCount})
                            <br />
                            &#x2022; Redundant heading level ({alertTitleCount})
                            <br />
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button size="small">Details</Button>
                        </CardActions>
                    </Card>
                  </Grid>
                </Grid>
                </Paper>
            </Grid>

            {/* <Grid item xs={12} md={6} lg={4}>
              <AppCurrentSubject
                title="Current Subject"
                chartLabels={['English', 'History', 'Physics', 'Geography', 'Chinese', 'Math']}
                chartData={[
                  { name: 'Series 1', data: [80, 50, 30, 40, 100, 20] },
                  { name: 'Series 2', data: [20, 30, 40, 80, 20, 80] },
                  { name: 'Series 3', data: [44, 76, 78, 13, 43, 10] },
                ]}
                chartColors={[...Array(6)].map(() => theme.palette.text.secondary)}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={8}>
              <AppNewsUpdate
                title="News Update"
                list={[...Array(5)].map((_, index) => ({
                  id: faker.datatype.uuid(),
                  title: faker.name.jobTitle(),
                  description: faker.name.jobTitle(),
                  image: `/assets/images/covers/cover_${index + 1}.jpg`,
                  postedAt: faker.date.recent(),
                }))}
              />
            </Grid> */}

            {/* <Grid item xs={12} md={6} lg={4}>
              <AppOrderTimeline
                title="Order Timeline"
                list={[...Array(5)].map((_, index) => ({
                  id: faker.datatype.uuid(),
                  title: [
                    '1983, orders, $4220',
                    '12 Invoices have been paid',
                    'Order #37745 from September',
                    'New order placed #XF-2356',
                    'New order placed #XF-2346',
                  ][index],
                  type: `order${index + 1}`,
                  time: faker.date.past(),
                }))}
              />
            </Grid> */}
{/* 
            <Grid item xs={12} md={6} lg={4}>
              <AppTrafficBySite
                title="Traffic by Site"
                list={[
                  {
                    name: 'FaceBook',
                    value: 323234,
                    icon: <Iconify icon={'eva:facebook-fill'} color="#1877F2" width={32} />,
                  },
                  {
                    name: 'Google',
                    value: 341212,
                    icon: <Iconify icon={'eva:google-fill'} color="#DF3E30" width={32} />,
                  },
                  {
                    name: 'Linkedin',
                    value: 411213,
                    icon: <Iconify icon={'eva:linkedin-fill'} color="#006097" width={32} />,
                  },
                  {
                    name: 'Twitter',
                    value: 443232,
                    icon: <Iconify icon={'eva:twitter-fill'} color="#1C9CEA" width={32} />,
                  },
                ]}
              />
            </Grid> */}

            <Grid item xs={12} md={6} lg={8}>
              <AppTasks
                title="To Do Tasks"
                list={[
                  { id: '1', label: 'Check for proper use of heading tags (H1-H6) to structure content hierarchy' },
                  { id: '2', label: 'Verify that all images have alt text that accurately describes their content and purpose' },
                  { id: '3', label: 'Test for color contrast ratios to ensure that text is legible for people with visual impairments' },
                  { id: '4', label: 'Check for keyboard accessibility, ensuring that all functionality can be accessed and used with only a keyboard' },
                  { id: '5', label: 'Verify that all video and audio content has transcripts or captions available for users with hearing impairments' },
                ]}
              />
            </Grid>
          </Grid>
          </div>
         )}
      </Container>
    </>
  );
}
